import { useState, useEffect } from "react";

// Material UI
import Dialog from "@mui/material/Dialog";

import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";

export default function DialogWithTextInput(props) {
    const [text, setText] = useState("");

    useEffect(() => {
        if (props.initialValue) {
            setText(props.initialValue);
        }
    }, [props.initialValue]);

    function handleCancelClick() {
        props.onClose(text, false);
        if (props.initialValue) {
            setText(props.initialValue);
        } else {
            setText("");
        }
    }

    function handleOKClick() {
        props.onClose(text, true);
        setText("");
    }

    const handleKeyboardInput = (e) => {
        setText(e.target.value);
    };

    if (props.modern) {
        return (
            <Dialog
                onClose={handleCancelClick}
                aria-labelledby="text-input-dialog-title"
                open={props.open}
                fullWidth
                maxWidth="xs"
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: 3,
                            overflow: "hidden",
                        },
                    },
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.5,
                        px: { xs: 2, sm: 3 },
                        py: 2.5,
                    }}
                >
                    {props.headerIcon && (
                        <Box
                            sx={{
                                width: 42,
                                height: 42,
                                display: "grid",
                                placeItems: "center",
                                flexShrink: 0,
                                borderRadius: 2,
                                color: "primary.main",
                                backgroundColor: "action.hover",
                            }}
                        >
                            {props.headerIcon}
                        </Box>
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            id="text-input-dialog-title"
                            variant="h5"
                            component="h2"
                            sx={{ lineHeight: 1.1 }}
                        >
                            {props.title}
                        </Typography>
                        {props.message && (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mt: 0.5 }}
                            >
                                {props.message}
                            </Typography>
                        )}
                    </Box>
                    <IconButton
                        aria-label="Close dialog"
                        onClick={handleCancelClick}
                        sx={{ mt: -0.75, mr: -1 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>

                <DialogContent
                    dividers
                    sx={{
                        p: { xs: 2, sm: 2.5 },
                        backgroundColor: "background.default",
                    }}
                >
                    <Box
                        sx={{
                            p: 2,
                            borderRadius: 2.5,
                            border: "1px solid",
                            borderColor: "divider",
                            backgroundColor: "background.paper",
                        }}
                    >
                        <TextField
                            value={text}
                            onChange={handleKeyboardInput}
                            onKeyDown={(event) => {
                                if (
                                    event.key === "Enter" &&
                                    text.trim() !== ""
                                ) {
                                    event.preventDefault();
                                    handleOKClick();
                                }
                            }}
                            autoFocus
                            id="name"
                            label={props.inputLabel || "Name"}
                            type="text"
                            variant="outlined"
                            fullWidth
                            inputProps={{
                                maxLength: props.maxLength,
                            }}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: 2,
                                },
                            }}
                        />
                    </Box>
                </DialogContent>

                <DialogActions
                    sx={{ px: { xs: 2, sm: 3 }, py: 1.5, gap: 1 }}
                >
                    <Button
                        id="cancel"
                        onClick={handleCancelClick}
                        color="inherit"
                    >
                        Cancel
                    </Button>
                    <Button
                        id="ok"
                        onClick={handleOKClick}
                        color="primary"
                        variant="contained"
                        disableElevation
                        disabled={text.trim() === ""}
                    >
                        {props.confirmLabel || "OK"}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    return (
        <>
            <Dialog
                onClose={handleCancelClick}
                aria-labelledby="simple-dialog-title"
                open={props.open}
            >
                <DialogTitle id="form-dialog-title">
                    {props.title}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>{props.message}</DialogContentText>
                    <TextField
                        value={text}
                        onChange={handleKeyboardInput}
                        autoFocus
                        margin="dense"
                        id="name"
                        label=""
                        type="text"
                        fullWidth
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        id="cancel"
                        onClick={handleCancelClick}
                        color="primary"
                    >
                        Cancel
                    </Button>
                    <Button
                        id="ok"
                        onClick={handleOKClick}
                        color="primary"
                        variant="text"
                    >
                        {props.confirmLabel || "OK"}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
